import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Col, Form, Row } from "antd";
import dayjs from "dayjs";
import * as React from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { schemaFormConstat } from "../../../features/forms/schemas/schemaFormConstat";
import { Audit } from "../../../types/audits";
import { ConstatGravity } from "../../../types/constatGravities";
import { Constat } from "../../../types/constats";
import { Site } from "../../../types/sites";
import { UserForList } from "../../../types/user";
import { isDev } from "../../../utils/dev";
import Br from "../../ui/Br";
import spacing from "../../ui/spacingConfig";
import FieldCheckbox from "../fields/FieldCheckbox";
import FieldDate from "../fields/FieldDate";
import FieldInput from "../fields/FieldInput";
import FieldSelect from "../fields/FieldSelect";
import FieldTextArea from "../fields/FieldTextArea";
import FieldUpload from "../fields/FieldUpload/FieldUpload";
import useSubmit from "./useSubmit";

export type FormConstatSchema = z.infer<typeof schemaFormConstat>;

type Props = {
  isEditMode?: boolean;
  constat?: Constat;
  sites?: Site[];
  audits?: Audit[];
  fixedAudit?: Audit;
  pilotes?: UserForList[];
  admins?: UserForList[];
  constatGravities?: ConstatGravity[];
};

const getDefaultValue = (
  edit: boolean,
  constat?: Constat,
  fixedAudit?: Audit,
): FormConstatSchema => {
  if (!edit || !constat) {
    return {
      eventType: "CONSTAT",
      name: "",
      description: "",
      site: fixedAudit?.site ? String(fixedAudit.site.id) : "",
      localisation: "",
      dateEvt: dayjs(new Date()),
      audit: fixedAudit ? String(fixedAudit.id) : "",
      pilote: "",
      gravity: "",
      needsCauseAnalysis: false,
      needsActions: false,
    };
  }
  return {
    eventType: "CONSTAT",
    name: constat.name || "",
    description: constat.description || "",
    site: constat.site ? String(constat.site.id) : "",
    localisation: constat.localisation,
    dateEvt: dayjs(constat.dateEvt),
    audit: String(constat.audit.id),
    pilote: constat.pilotes?.[0] ? String(constat.pilotes[0].id) : "",
    gravity: constat.gravity ? String(constat.gravity.id) : "",
    needsCauseAnalysis: constat.needsCauseAnalysis,
    needsActions: constat.needsActions,
  };
};

const FormConstatNew: React.FC<Props> = ({
  isEditMode,
  constat,
  sites,
  audits,
  fixedAudit,
  pilotes,
  admins,
  constatGravities,
}) => {
  const { onSubmit, isLoading } = useSubmit(isEditMode, constat);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormConstatSchema>({
    defaultValues: getDefaultValue(!!isEditMode, constat, fixedAudit),
    resolver: zodResolver(schemaFormConstat),
  });

  if (isDev) {
    const formValues = watch();
    console.log("formValues", formValues);
    console.log("errors", errors);
  }

  const auditOptions = fixedAudit
    ? [{ value: String(fixedAudit.id), label: fixedAudit.title }]
    : audits?.map((audit) => ({ value: String(audit.id), label: audit.title })) ||
      [];

  return (
    <Form
      labelCol={{ span: spacing.tiny }}
      wrapperCol={{ span: spacing.sPlus }}
      layout="horizontal"
      onFinish={handleSubmit(onSubmit)}
      labelAlign="left"
      colon={false}
    >
      <Row gutter={spacing.m}>
        <Col span={14}>
          <Card title="Description" bordered={false}>
            <FieldInput<FormConstatSchema>
              errorMessage={errors?.name?.message}
              control={control}
              name="name"
              label="Titre"
              placeholder="Titre..."
            />
            <FieldTextArea<FormConstatSchema>
              errorMessage={errors?.description?.message}
              control={control}
              name="description"
              label="Description"
              placeholder="Description..."
            />
          </Card>
        </Col>
        <Col span={10}>
          <Card title="Date et lieu" bordered={false}>
            <FieldSelect<FormConstatSchema>
              errorMessage={errors?.audit?.message}
              control={control}
              name="audit"
              label="Suivi des Audits"
              placeholder="Choisir un audit"
              disabled={!!fixedAudit || isEditMode}
              options={auditOptions}
            />
            <FieldSelect<FormConstatSchema>
              errorMessage={errors?.site?.message}
              control={control}
              name="site"
              label="Site"
              placeholder="Choisir un site"
              options={
                sites?.map((site) => ({
                  value: String(site.id),
                  label: site.name,
                })) || []
              }
            />
            <FieldInput<FormConstatSchema>
              errorMessage={errors?.localisation?.message}
              control={control}
              name="localisation"
              label="Localisation"
              placeholder="PrÃ©cision..."
            />
            <FieldDate<FormConstatSchema>
              errorMessage={errors?.dateEvt?.message}
              control={control}
              name="dateEvt"
              label="Date"
            />
          </Card>
        </Col>
      </Row>
      <Br height={spacing.xl} />
      <Row gutter={spacing.m}>
        <Col span={14}>
          <Card title="Pilote et gravitÃ©" bordered={false}>
            <FieldSelect<FormConstatSchema>
              errorMessage={errors?.pilote?.message}
              control={control}
              name="pilote"
              label="Pilote"
              placeholder="Attribuer Ã "
              options={
                pilotes?.concat(admins || []).map((user) => ({
                  value: String(user.id),
                  label: user.fullname,
                })) || []
              }
            />
            <FieldSelect<FormConstatSchema>
              errorMessage={errors?.gravity?.message}
              control={control}
              name="gravity"
              label="GravitÃ©"
              placeholder="SÃ©lectionner une gravitÃ©"
              options={
                constatGravities?.map((gravity) => ({
                  value: String(gravity.id),
                  label: gravity.name,
                })) || []
              }
            />
            <FieldCheckbox<FormConstatSchema>
              errorMessage={errors?.needsCauseAnalysis?.message}
              control={control}
              name="needsCauseAnalysis"
              label="Analyse de cause nÃ©cessaire"
              placeholder="Analyse de cause nÃ©cessaire"
              smallLabel
            />
            <FieldCheckbox<FormConstatSchema>
              errorMessage={errors?.needsActions?.message}
              control={control}
              name="needsActions"
              label="Actions nÃ©cessaires"
              placeholder="Actions nÃ©cessaires"
              smallLabel
            />
          </Card>
        </Col>
      </Row>
      <Br height={spacing.xl} />
      <Row>
        <Col span={spacing.m}>
          <Card title="PiÃ¨ces jointes" bordered={false}>
            <FieldUpload<FormConstatSchema>
              errorMessage={errors.attachedFiles?.message}
              name="attachedFiles"
              control={control}
              listType="picture"
              label=""
              placeholder=""
            />
          </Card>
        </Col>
      </Row>
      <Br height={spacing.xl} />
      <Row justify="end">
        <Button
          type="primary"
          htmlType="submit"
          className="large-padding-btn"
          loading={isLoading}
        >
          {isEditMode ? "Modifier" : "Publier"}
        </Button>
      </Row>
    </Form>
  );
};

export default FormConstatNew;
